import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";

const TOWN_PAGES = {
    Birmingham: "birmingham",
    Coventry: "coventry",
    Wolverhampton: "wolverhampton",
    Leicester: "leicester",
    Nottingham: "nottingham",
    Derby: "derby",
    Lincoln: "lincoln",
};

const WEST = [
    "Birmingham", "Coventry", "Wolverhampton", "Walsall", "Dudley", "Solihull",
    "West Bromwich", "Sutton Coldfield", "Stoke-on-Trent", "Worcester", "Telford", "Shrewsbury",
];

const EAST = [
    "Leicester", "Nottingham", "Derby", "Lincoln", "Loughborough", "Mansfield",
    "Chesterfield", "Burton upon Trent", "Northampton", "Nuneaton", "Rugby", "Tamworth",
    "Stafford", "Grantham", "Boston",
];

const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
};

function TownGrid({ towns, label, testid }) {
    return (
        <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-6 flex items-center gap-2">
                <MapPin size={14} /> {label}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 border-t border-l border-ash" data-testid={testid}>
                {towns.map((town, i) => {
                    const slug = TOWN_PAGES[town];
                    const cls = "block border-b border-r border-ash px-4 py-4 font-mono text-xs sm:text-sm tracking-[0.15em] uppercase transition-colors duration-200 " +
                        (slug ? "text-paper hover:text-tarmac hover:bg-amber" : "text-grit hover:text-tarmac hover:bg-amber cursor-default");
                    const inner = (
                        <span className="flex items-center justify-between gap-2">
                            {town}
                            {slug && <ArrowUpRight size={12} className="text-amber shrink-0" />}
                        </span>
                    );
                    return (
                        <motion.div key={town} {...reveal} transition={{ duration: 0.4, delay: i * 0.04 }}>
                            {slug ? (
                                <Link to={`/areas/${slug}`} data-testid={`coverage-link-${slug}`} className={cls}>{inner}</Link>
                            ) : (
                                <span className={cls}>{inner}</span>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}

export default function CoverageArea() {
    return (
        <section id="coverage" className="relative bg-tarmac py-24 sm:py-32 grain border-t border-ash" data-testid="coverage-section">
            <div className="px-5 sm:px-8 max-w-7xl mx-auto relative z-10">
                <motion.p {...reveal} transition={{ duration: 0.6 }} className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-4">
                    [ Where we work ]
                </motion.p>
                <motion.h2 {...reveal} transition={{ duration: 0.7 }} className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-7xl mb-6">
                    The whole of<br /><span className="text-amber">the Midlands & Lincs.</span>
                </motion.h2>
                <motion.p {...reveal} transition={{ duration: 0.7, delay: 0.1 }} className="text-base lg:text-lg text-grit leading-relaxed max-w-xl mb-16">
                    From Birmingham to Nottingham, Stoke to Lincoln and across
                    Lincolnshire — if your kerb is in the Midlands, we'll survey it,
                    permit it and drop it. Don't see your town? Call us anyway.
                </motion.p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <TownGrid towns={WEST} label="West Midlands" testid="coverage-west-grid" />
                    <TownGrid towns={EAST} label="East Midlands" testid="coverage-east-grid" />
                </div>
            </div>
        </section>
    );
}
